import { useState } from 'react'
import { motion } from 'framer-motion'
import { 
  UserPlus, 
  Mail, 
  Shield,
  CheckCircle,
  Send,
  User,
  Lock,
  Eye,
  EyeOff
} from 'lucide-react'
import { useStore } from '../../store/useStore'
import GlassCard from '../../components/ui/GlassCard'
import Button from '../../components/ui/Button'
import AdminInput from '../../components/ui/AdminInput'
import AdminLayout from '../../components/admin/AdminLayout'

const AdminManagement = () => {
  const { addAdmin } = useStore()
  const [showForm, setShowForm] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    role: 'admin',
    permissions: {
      jobs: true,
      gallery: true,
      applications: true,
      settings: false
    }
  })

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      // Simuler l'envoi de la demande
      await new Promise(resolve => setTimeout(resolve, 2000))
      
      // Envoyer l'email de demande d'approbation
      const approvalEmail = {
        to: 's.massena@videotron.ca',
        subject: `Demande d'ajout d'administrateur - ${formData.firstName} ${formData.lastName}`,
        body: `
          <h2>Nouvelle demande d'administrateur</h2>
          <p><strong>Nom:</strong> ${formData.firstName} ${formData.lastName}</p>
          <p><strong>Email:</strong> ${formData.email}</p>
          <p><strong>Rôle:</strong> ${formData.role}</p>
          <p><strong>Permissions:</strong></p>
          <ul>
            ${formData.permissions.jobs ? '<li>Gestion des emplois</li>' : ''}
            ${formData.permissions.gallery ? '<li>Gestion de la galerie</li>' : ''}
            ${formData.permissions.applications ? '<li>Gestion des candidatures</li>' : ''}
            ${formData.permissions.settings ? '<li>Paramètres</li>' : ''}
          </ul>
          <p><strong>Action requise:</strong> Approuver ou rejeter cette demande</p>
          <p><strong>Lien d'approbation:</strong> <a href="#">Approuver</a></p>
        `
      }

      console.log('Email d\'approbation envoyé:', approvalEmail)
      
      setSubmitted(true)
      setShowForm(false)
      
    } catch (error) {
      console.error('Erreur lors de l\'envoi de la demande:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handlePermissionChange = (permission) => {
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [permission]: !prev.permissions[permission]
      }
    }))
  }

  const handleFirstNameChange = (e) => {
    setFormData(prev => ({ ...prev, firstName: e.target.value }))
  }

  const handleLastNameChange = (e) => {
    setFormData(prev => ({ ...prev, lastName: e.target.value }))
  }

  const handleEmailChange = (e) => {
    setFormData(prev => ({ ...prev, email: e.target.value }))
  }

  const handlePasswordChange = (e) => {
    setFormData(prev => ({ ...prev, password: e.target.value }))
  }

  if (submitted) {
    return (
      <AdminLayout>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
    <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-md mx-auto text-center"
          >
            <GlassCard className="p-8">
              <CheckCircle size={64} className="text-green-600 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Demande Envoyée !
              </h2>
              <p className="text-gray-600 mb-4">
                La demande d'ajout d'administrateur pour <strong>{formData.firstName} {formData.lastName}</strong> a été envoyée.
              </p>
              <p className="text-sm text-gray-500 mb-6">
                Un email de validation a été envoyé à <strong>s.massena@videotron.ca</strong> pour approbation.
              </p>
            <Button
                onClick={() => {
                  setSubmitted(false)
                  setFormData({
                    firstName: '',
                    lastName: '',
                    email: '',
                    password: '',
                    role: 'admin',
                    permissions: {
                      jobs: true,
                      gallery: true,
                      applications: true,
                      settings: false
                    }
                  })
                }}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Ajouter un autre admin
            </Button>
      </GlassCard>
    </motion.div>
        </div>
      </AdminLayout>
    )
  }

  return (
    <AdminLayout>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-4xl mx-auto"
        >
          {/* En-tête */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center">
              <UserPlus size={32} className="mr-3 text-blue-600" />
              Gestion des Administrateurs
            </h1>
            <p className="text-gray-600">
              Ajoutez de nouveaux administrateurs. Une demande d'approbation sera envoyée par email.
            </p>
          </div>

          {/* Formulaire */}
          <GlassCard className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                <UserPlus size={20} className="mr-2" />
                Nouvel Administrateur
              </h2>
            <Button
                onClick={() => setShowForm(!showForm)}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {showForm ? 'Annuler' : 'Ajouter un Admin'}
            </Button>
        </div>

            {showForm && (
              <motion.form
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                onSubmit={handleSubmit}
                className="space-y-6"
              >
                {/* Informations personnelles */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <AdminInput
                    label="Prénom"
                    value={formData.firstName}
                    onChange={handleFirstNameChange}
                    placeholder="Jean"
                    required
                  />
                  
                  <AdminInput
                    label="Nom de famille"
                    value={formData.lastName}
                    onChange={handleLastNameChange}
                    placeholder="Dupont"
                    required
                  />
          </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <AdminInput
                    label="Email"
                    type="email"
                    value={formData.email}
                    onChange={handleEmailChange}
                    placeholder="jean.dupont@example.com"
                    required
                  />
                  
                  <div className="relative">
                    <AdminInput
                      label="Mot de passe temporaire"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={handlePasswordChange}
                      placeholder="Mot de passe temporaire"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-8 text-gray-400 hover:text-gray-600"
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                      </div>
                    </div>
                    
                {/* Permissions */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Permissions
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {Object.entries(formData.permissions).map(([permission, enabled]) => (
                      <label key={permission} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={enabled}
                          onChange={() => handlePermissionChange(permission)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700 capitalize">
                          {permission === 'jobs' && 'Emplois'}
                          {permission === 'gallery' && 'Galerie'}
                          {permission === 'applications' && 'Candidatures'}
                          {permission === 'settings' && 'Paramètres'}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Note sur l'approbation */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <Mail size={20} className="text-blue-600 mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-blue-900 mb-1">
                        Processus d'approbation
                      </h3>
                      <p className="text-blue-800 text-sm">
                        Une demande d'approbation sera automatiquement envoyée à <strong>s.massena@videotron.ca</strong>. 
                        L'administrateur ne pourra accéder au système qu'après approbation.
                      </p>
                    </div>
                  </div>
            </div>

                {/* Boutons */}
                <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
                  <Button
                    type="button"
                    onClick={() => setShowForm(false)}
                    variant="outline"
                  >
                    Annuler
                  </Button>
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        Envoi en cours...
                      </>
                    ) : (
                      <>
                        <Send size={16} className="mr-2" />
                        Envoyer la Demande
                      </>
                    )}
                  </Button>
            </div>
              </motion.form>
            )}
        </GlassCard>
        </motion.div>
      </div>
    </AdminLayout>
  )
}

export default AdminManagement